import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { 
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Stop as InactiveIcon
} from '@mui/icons-material';

export interface AlertStatusProps {
  status: 'success' | 'warning' | 'error' | 'info' | 'inactive';
  executionTime?: number;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  showExecutionTime?: boolean;
}

const AlertStatus: React.FC<AlertStatusProps> = ({
  status,
  executionTime,
  label,
  size = 'medium',
  showExecutionTime = true
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: <SuccessIcon />,
          color: 'success' as const,
          text: 'Success',
          executionTimeColor: executionTime && executionTime < 2 ? 'success' : 
                             executionTime && executionTime < 5 ? 'warning' : 'error'
        };
      case 'warning':
        return {
          icon: <WarningIcon />,
          color: 'warning' as const,
          text: 'Warning',
          executionTimeColor: 'warning' as const
        };
      case 'error':
        return {
          icon: <ErrorIcon />,
          color: 'error' as const,
          text: 'Error',
          executionTimeColor: 'error' as const
        };
      case 'info':
        return {
          icon: <InfoIcon />,
          color: 'info' as const,
          text: 'Info',
          executionTimeColor: 'info' as const
        };
      case 'inactive':
        return {
          icon: <InactiveIcon />,
          color: 'default' as const,
          text: 'Inactive',
          executionTimeColor: 'default' as const
        };
      default:
        return {
          icon: <InfoIcon />,
          color: 'info' as const,
          text: 'Unknown',
          executionTimeColor: 'info' as const
        };
    }
  };

  const config = getStatusConfig();
  const displayLabel = label || config.text;

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { iconSize: 16, fontSize: '0.75rem', padding: '4px 8px' };
      case 'large':
        return { iconSize: 24, fontSize: '1rem', padding: '8px 16px' };
      default:
        return { iconSize: 20, fontSize: '0.875rem', padding: '6px 12px' };
    }
  };

  const sizeProps = getSizeProps();

  const formatExecutionTime = (time: number) => {
    if (time < 1) return `${(time * 1000).toFixed(0)}ms`;
    return `${time.toFixed(1)}s`;
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip
        icon={config.icon}
        label={displayLabel}
        color={config.color}
        size={size === 'small' ? 'small' : 'medium'}
        sx={{
          '& .MuiChip-icon': {
            fontSize: sizeProps.iconSize,
          },
          '& .MuiChip-label': {
            fontSize: sizeProps.fontSize,
            fontWeight: 500,
          },
          padding: sizeProps.padding,
        }}
      />
      {showExecutionTime && executionTime !== undefined && (
        <Typography
          variant="body2"
          color={`${config.executionTimeColor}.main`}
          sx={{
            fontWeight: 500,
            fontSize: sizeProps.fontSize,
          }}
        >
          {formatExecutionTime(executionTime)}
        </Typography>
      )}
    </Box>
  );
};

export default AlertStatus;
